/**
 * 순수 Alpine.js 기반 DatePicker (flatpickr 의존성 없음)
 *
 * 사용법:
 *   <div x-data="fineDatePicker()">                              // 기본 설정
 *   <div x-data="fineDatePicker({ format: 'YYYY-MM-DD' })">      // 날짜 형식 지정
 *   <div x-data="fineDatePicker({ defaultDate: '2024-01-01' })"> // 초기 날짜 지정
 *   <div x-data="fineDatePicker({ confirmMode: false })">        // 버튼 없이 즉시 반영
 *
 * 옵션:
 *   - format: 날짜 형식 ('YYYY-MM-DD', 'MM-DD-YYYY', 'DD-MM-YYYY', 'M d, Y', 'D d M, Y')
 *   - defaultDate: 초기 날짜 (Date 객체 또는 'YYYY-MM-DD' 문자열)
 *   - confirmMode: 등록/취소 버튼 모드 (기본: true)
 *   - confirmText: 등록 버튼 텍스트 (기본: '등록')
 *   - cancelText: 취소 버튼 텍스트 (기본: '취소')
 *   - placeholder: input placeholder (기본: '날짜 선택')
 *   - inputClass: input 추가 클래스
 *   - onChange: 날짜 변경 콜백 (dateStr) => {}
 *   - onConfirm: 등록 콜백 (dateStr) => {}
 *   - onCancel: 취소 콜백 () => {}
 */
export default (options = {}) => ({
    // 상태
    open: false,
    value: '',
    tempValue: '',
    month: 0,
    year: 0,
    day: 0,
    tempDay: 0,
    daysInMonth: [],
    blankDaysInMonth: [],

    // 상수
    monthNames: ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'],
    dayNames: ['일', '월', '화', '수', '목', '금', '토'],

    // 옵션
    options: {
        format: 'YYYY-MM-DD',
        confirmMode: true,
        confirmText: '등록',
        cancelText: '취소',
        placeholder: '날짜 선택',
        inputClass: '',
        allowEmpty: false,  // true면 초기값 없이 빈 상태로 시작
        autoOpen: false,    // true면 초기화 시 자동으로 캘린더 열기
        hideInput: false,   // true면 입력창 숨기기 (캘린더만 표시)
        weekStartsOn: 0,    // 0=일요일, 1=월요일 (요일 헤더/blank 계산 시작점)
        weekSelect: false,  // true면 주 단위 선택 모드 — hover 시 행 전체 강조, click 시 (startStr, endStr) onConfirm 전달
        ...options
    },

    // 주 단위 선택 모드 상태
    tempWeekStart: null,
    tempWeekEnd: null,
    weekStart: null,
    weekEnd: null,

    // 캘린더 컨테이너
    calendarEl: null,

    init() {
        // allowEmpty 옵션이 true이고 defaultDate가 없으면 빈 상태로 시작
        if (this.options.allowEmpty && !this.options.defaultDate) {
            this.value = '';
            this.tempValue = '';
            const now = new Date();
            this.month = now.getMonth();
            this.year = now.getFullYear();
            this.day = 0;
            this.tempDay = 0;
        } else {
            // 초기 날짜 설정 (기존 로직)
            let currentDate = new Date();
            if (this.options.defaultDate) {
                currentDate = new Date(Date.parse(this.options.defaultDate));
            }

            this.month = currentDate.getMonth();
            this.year = currentDate.getFullYear();
            this.day = currentDate.getDate();
            this.value = this.formatDate(currentDate);
            this.tempValue = this.value;
            this.tempDay = this.day;
        }
        this.calculateDays();

        // weekSelect 모드: 초기 주 계산
        if (this.options.weekSelect) {
            const seed = this.value
                ? new Date(this.year, this.month, this.day || 1)
                : new Date();
            this._setWeekFromDate(seed, /* commit */ true);
        }

        // 캘린더 UI 생성
        this._createCalendarUI();

        // ESC 키 핸들러
        this.$el.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.open) {
                this.cancel();
            }
        });

        // 외부 클릭 핸들러
        document.addEventListener('click', (e) => {
            if (this.open && !this.$el.contains(e.target)) {
                this.cancel();
            }
        });

        // 외부에서 날짜 설정 이벤트 수신 (set-date)
        this.$el.addEventListener('set-date', (e) => {
            if (e.detail && e.detail.date) {
                this.setDate(e.detail.date);
            }
        });

        // autoOpen 옵션이 true면 자동으로 캘린더 열기
        if (this.options.autoOpen) {
            this.$nextTick(() => {
                this.open = true;
            });
        }
    },

    _createCalendarUI() {
        // input 요소 찾기 또는 생성
        let inputEl = this.$refs.input;
        if (!inputEl) {
            inputEl = this.$el.querySelector('input');
        }

        // input이 없으면 생성
        if (!inputEl) {
            const inputWrapper = document.createElement('div');
            inputWrapper.className = 'relative w-[17rem]';
            inputWrapper.innerHTML = `
                <input type="text"
                       readonly
                       placeholder="${this.options.placeholder}"
                       class="flex px-3 py-2 w-full h-10 text-sm bg-white rounded-md border text-neutral-600 border-neutral-300 ring-offset-background placeholder:text-neutral-400 focus:border-neutral-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-neutral-400 disabled:cursor-not-allowed disabled:opacity-50 ${this.options.inputClass}" />
                <div class="absolute top-0 right-0 px-3 py-2 cursor-pointer text-neutral-400 hover:text-neutral-500">
                    <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                </div>
            `;
            this.$el.appendChild(inputWrapper);
            inputEl = inputWrapper.querySelector('input');

            // hideInput 옵션이 true면 입력창 숨기기
            if (this.options.hideInput) {
                inputWrapper.style.display = 'none';
            }

            // 클릭 이벤트
            inputWrapper.addEventListener('click', () => this.toggle());
        }

        // input 값 바인딩
        this.$watch('value', (val) => {
            inputEl.value = val;
        });
        inputEl.value = this.value;

        // 캘린더 컨테이너 생성
        this.calendarEl = document.createElement('div');
        this.calendarEl.className = 'absolute top-0 left-0 z-50 max-w-lg p-4 mt-12 antialiased bg-white border rounded-lg shadow w-[17rem] border-neutral-200/70';
        this.calendarEl.style.display = 'none';

        // 캘린더 내부 클릭 시 이벤트 버블링 중단 (외부 클릭 핸들러 오작동 방지)
        this.calendarEl.addEventListener('click', (e) => {
            e.stopPropagation();
        });

        // this.$el에 캘린더 추가 (input 위치와 독립적으로 표시)
        this.$el.style.position = 'relative';
        this.$el.appendChild(this.calendarEl);

        // open 상태 감시
        this.$watch('open', (isOpen) => {
            this.calendarEl.style.display = isOpen ? 'block' : 'none';
            if (isOpen) {
                this._renderCalendar();
                this._adjustPosition();
            }
        });
    },

    _adjustPosition() {
        if (!this.calendarEl) return;

        // hideInput 모드일 때는 위치 조정 없이 그대로 표시
        if (this.options.hideInput) {
            this.calendarEl.style.top = '0';
            this.calendarEl.style.left = '0';
            this.calendarEl.style.bottom = 'auto';
            this.calendarEl.style.marginTop = '0';
            this.calendarEl.style.marginBottom = '0';
            return;
        }

        // this.$el 기준으로 위치 계산 (hidden input 문제 회피)
        const elRect = this.$el.getBoundingClientRect();
        const calendarHeight = this.calendarEl.offsetHeight;
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - elRect.bottom;
        const spaceAbove = elRect.top;

        // 아래 공간이 부족하고 위 공간이 충분하면 dropup
        if (spaceBelow < calendarHeight + 20 && spaceAbove > calendarHeight + 20) {
            // dropup: input 위로
            this.calendarEl.style.top = 'auto';
            this.calendarEl.style.bottom = '100%';
            this.calendarEl.style.marginTop = '0';
            this.calendarEl.style.marginBottom = '0.5rem';
        } else {
            // dropdown: input 아래로 (기본)
            this.calendarEl.style.top = '0';
            this.calendarEl.style.bottom = 'auto';
            this.calendarEl.style.marginTop = '3rem';
            this.calendarEl.style.marginBottom = '0';
        }
    },

    _renderCalendar() {
        const confirmMode = this.options.confirmMode;
        const confirmText = this.options.confirmText;
        const cancelText = this.options.cancelText;

        // 헤더
        let html = `
            <div class="flex justify-between items-center mb-2">
                <div>
                    <span class="ml-1 text-lg font-normal text-gray-600">${this.year}</span>
                    <span class="text-lg font-bold text-gray-800">${this.monthNames[this.month]}</span>
                </div>
                <div>
                    <button type="button" class="inline-flex p-1 rounded-full transition duration-100 ease-in-out cursor-pointer focus:outline-none focus:shadow-outline hover:bg-gray-100" data-action="prev">
                        <svg class="inline-flex w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <button type="button" class="inline-flex p-1 rounded-full transition duration-100 ease-in-out cursor-pointer focus:outline-none focus:shadow-outline hover:bg-gray-100" data-action="next">
                        <svg class="inline-flex w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>
            </div>
        `;

        // 요일 헤더 (weekStartsOn 기준으로 회전)
        const wso = this.options.weekStartsOn;
        const rotatedDayNames = this.dayNames.slice(wso).concat(this.dayNames.slice(0, wso));
        html += '<div class="grid grid-cols-7 mb-2">';
        rotatedDayNames.forEach(day => {
            html += `<div class="px-0.5"><div class="text-xs font-medium text-center text-gray-800">${day}</div></div>`;
        });
        html += '</div>';

        // 날짜 그리드
        html += '<div class="grid grid-cols-7">';

        // 셀에 부여할 행(주) 인덱스 계산용 카운터
        let cellIndex = 0;

        // 빈 칸
        this.blankDaysInMonth.forEach(() => {
            const weekRow = Math.floor(cellIndex / 7);
            html += `<div class="p-1 text-sm text-center border border-transparent" data-week-row="${weekRow}"></div>`;
            cellIndex++;
        });

        // 날짜
        this.daysInMonth.forEach(day => {
            const weekRow = Math.floor(cellIndex / 7);
            const isToday = this.isToday(day);
            const isSelected = this.isSelectedDate(day);

            let dayClass = 'flex justify-center items-center w-7 h-7 text-sm leading-none text-center rounded-full cursor-pointer';
            if (isSelected) {
                dayClass += ' bg-neutral-800 text-white hover:bg-neutral-800/70';
            } else if (isToday) {
                dayClass += ' bg-neutral-200';
            } else {
                dayClass += ' text-gray-600 hover:bg-neutral-200';
            }

            html += `
                <div class="px-0.5 mb-0.5 aspect-square" data-week-row="${weekRow}">
                    <div class="${dayClass}" data-day="${day}">${day}</div>
                </div>
            `;
            cellIndex++;
        });
        html += '</div>';

        // 버튼 영역 (confirmMode일 때만)
        if (confirmMode) {
            html += `
                <div class="flex justify-end gap-2 pt-3 mt-3 border-t border-neutral-200">
                    <button type="button" class="px-3 py-1.5 text-sm font-medium text-white bg-neutral-800 rounded-md hover:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-neutral-500" data-action="confirm">
                        ${confirmText}
                    </button>
                    <button type="button" class="px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300" data-action="cancel">
                        ${cancelText}
                    </button>
                </div>
            `;
        }

        this.calendarEl.innerHTML = html;

        // weekSelect: 행 단위 hover 강조
        if (this.options.weekSelect) {
            const rowCells = this.calendarEl.querySelectorAll('[data-week-row]');
            rowCells.forEach(cell => {
                cell.addEventListener('mouseenter', () => {
                    const row = cell.dataset.weekRow;
                    this.calendarEl.querySelectorAll(`[data-week-row="${row}"]`).forEach(c => {
                        c.classList.add('bg-neutral-100');
                    });
                });
                cell.addEventListener('mouseleave', () => {
                    const row = cell.dataset.weekRow;
                    this.calendarEl.querySelectorAll(`[data-week-row="${row}"]`).forEach(c => {
                        c.classList.remove('bg-neutral-100');
                    });
                });
            });
        }

        // 이벤트 바인딩
        this.calendarEl.querySelectorAll('[data-day]').forEach(el => {
            el.addEventListener('click', () => {
                const day = parseInt(el.dataset.day);
                this.dayClicked(day);
                if (!confirmMode) {
                    this.confirm();
                } else {
                    this._renderCalendar(); // 선택 상태 업데이트
                }
            });
        });

        this.calendarEl.querySelector('[data-action="prev"]')?.addEventListener('click', (e) => {
            e.stopPropagation();
            this.previousMonth();
        });

        this.calendarEl.querySelector('[data-action="next"]')?.addEventListener('click', (e) => {
            e.stopPropagation();
            this.nextMonth();
        });

        this.calendarEl.querySelector('[data-action="confirm"]')?.addEventListener('click', (e) => {
            e.stopPropagation();
            this.confirm();
        });

        this.calendarEl.querySelector('[data-action="cancel"]')?.addEventListener('click', (e) => {
            e.stopPropagation();
            this.cancel();
        });
    },

    toggle() {
        this.open = !this.open;
    },

    dayClicked(day) {
        const selectedDate = new Date(this.year, this.month, day);
        this.tempDay = day;
        this.tempValue = this.formatDate(selectedDate);

        // weekSelect 모드: 클릭한 날이 속한 주의 시작/끝 갱신
        if (this.options.weekSelect) {
            this._setWeekFromDate(selectedDate, /* commit */ false);
        }
    },

    confirm() {
        this.value = this.tempValue;
        this.day = this.tempDay;
        this.open = false;

        // weekSelect 모드: 주 단위 확정
        if (this.options.weekSelect) {
            this.weekStart = this.tempWeekStart;
            this.weekEnd = this.tempWeekEnd;
            const startStr = this.formatDate(this.weekStart);
            const endStr = this.formatDate(this.weekEnd);
            if (this.options.onConfirm) {
                this.options.onConfirm(startStr, endStr);
            }
            if (this.options.onChange) {
                this.options.onChange(startStr, endStr);
            }
            return;
        }

        if (this.options.onConfirm) {
            this.options.onConfirm(this.value);
        }
        if (this.options.onChange) {
            this.options.onChange(this.value);
        }
    },

    // weekSelect 헬퍼: 임의의 날짜로부터 그 주의 시작/끝 계산
    _setWeekFromDate(date, commit) {
        const dow = date.getDay();
        const offset = ((dow - this.options.weekStartsOn) + 7) % 7;
        const start = new Date(date.getFullYear(), date.getMonth(), date.getDate() - offset);
        const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
        this.tempWeekStart = start;
        this.tempWeekEnd = end;
        if (commit) {
            this.weekStart = start;
            this.weekEnd = end;
        }
    },

    cancel() {
        this.tempValue = this.value;
        this.tempDay = this.day;
        this.open = false;

        // weekSelect 모드: 임시 주 선택 되돌리기
        if (this.options.weekSelect) {
            this.tempWeekStart = this.weekStart;
            this.tempWeekEnd = this.weekEnd;
        }

        if (this.options.onCancel) {
            this.options.onCancel();
        }
    },

    previousMonth() {
        if (this.month === 0) {
            this.year--;
            this.month = 11;
        } else {
            this.month--;
        }
        this.calculateDays();
        this._renderCalendar();
    },

    nextMonth() {
        if (this.month === 11) {
            this.month = 0;
            this.year++;
        } else {
            this.month++;
        }
        this.calculateDays();
        this._renderCalendar();
    },

    isSelectedDate(day) {
        const d = new Date(this.year, this.month, day);
        // weekSelect: 선택된 주의 범위에 속하면 강조
        if (this.options.weekSelect && this.tempWeekStart && this.tempWeekEnd) {
            return d >= this.tempWeekStart && d <= this.tempWeekEnd;
        }
        return this.tempValue === this.formatDate(d);
    },

    isToday(day) {
        const today = new Date();
        const d = new Date(this.year, this.month, day);
        return today.toDateString() === d.toDateString();
    },

    calculateDays() {
        const daysInMonth = new Date(this.year, this.month + 1, 0).getDate();
        const dayOfWeek = new Date(this.year, this.month, 1).getDay();
        // weekStartsOn 기준으로 1일 앞의 빈 칸 수 계산 (0=일, 1=월, ...)
        const startOffset = ((dayOfWeek - this.options.weekStartsOn) + 7) % 7;

        const blankDays = [];
        for (let i = 0; i < startOffset; i++) {
            blankDays.push(i);
        }

        const days = [];
        for (let i = 1; i <= daysInMonth; i++) {
            days.push(i);
        }

        this.blankDaysInMonth = blankDays;
        this.daysInMonth = days;
    },

    formatDate(date) {
        const dayName = this.dayNames[date.getDay()];
        const dd = ('0' + date.getDate()).slice(-2);
        const monthName = this.monthNames[date.getMonth()];
        const monthShort = monthName.substring(0, 2);
        const mm = ('0' + (date.getMonth() + 1)).slice(-2);
        const yyyy = date.getFullYear();

        const format = this.options.format;

        if (format === 'M d, Y') {
            return `${monthShort} ${dd}, ${yyyy}`;
        }
        if (format === 'MM-DD-YYYY') {
            return `${mm}-${dd}-${yyyy}`;
        }
        if (format === 'DD-MM-YYYY') {
            return `${dd}-${mm}-${yyyy}`;
        }
        if (format === 'YYYY-MM-DD') {
            return `${yyyy}-${mm}-${dd}`;
        }
        if (format === 'D d M, Y') {
            return `${dayName} ${dd} ${monthShort} ${yyyy}`;
        }

        return `${monthName} ${dd}, ${yyyy}`;
    },

    // 외부에서 날짜 설정
    setDate(dateStr) {
        const date = new Date(Date.parse(dateStr));
        this.month = date.getMonth();
        this.year = date.getFullYear();
        this.day = date.getDate();
        this.value = this.formatDate(date);
        this.tempValue = this.value;
        this.tempDay = this.day;
        this.calculateDays();

        // input 요소에 직접 값 설정
        const inputEl = this.$el.querySelector('input');
        if (inputEl) {
            inputEl.value = this.value;
        }
    },

    // 날짜 초기화
    clearDate() {
        this.value = '';
        this.tempValue = '';
        const now = new Date();
        this.month = now.getMonth();
        this.year = now.getFullYear();
        this.calculateDays();
    }
});
